// src/helpers/rutaClienterecepcion.js
import axios from "axios";

const RAW_BASE_URL =
  process.env.REACT_APP_API_BASE || "https://backre-miami.us-3.evennode.com";

const BASE_URL = String(RAW_BASE_URL).replace(/\/+$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

// normaliza rutas: "clienterecepcion" => "/clienterecepcion"
const p = (path) => `/${String(path || "").replace(/^\/+/, "")}`;

const getToken = () => localStorage.getItem("token") || "";

const auth = () => {
  const token = getToken();
  return token ? { headers: { token } } : {};
};

const debug =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase() === "true";

const logUrl = (url, params, data, method = "GET") => {
  if (!debug) return;

  console.log("👉 METHOD:", method);
  console.log("👉 URL:", API.getUri({ url, params }));

  if (data) {
    console.log("📦 DATA ENVIADA:", data);
  }
};

async function req(method, url, { params, data, signal } = {}) {
  logUrl(url, params, data, method);

  try {
    const res = await API.request({
      method,
      url,
      params,
      data,
      signal,
      ...auth(),
    });

    return res.data;
  } catch (err) {
    if (debug) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      console.log("🧯 API ERROR:", msg);
      console.log("🧯 STATUS:", err?.response?.status);
      console.log("🧯 DATA:", err?.response?.data);
    }

    throw err;
  }
}

/* ----------------------------- NORMALIZADORES ---------------------------- */

const clean = (v) => String(v ?? "").trim();

const buildNombreCompleto = (src = {}) => {
  const nombre = clean(src.nombre ?? src.firstName);
  const segundoNombre = clean(src.segundoNombre ?? src.middleName);
  const apellido1 = clean(src.apellido1 ?? src.lastName);
  const apellido2 = clean(src.apellido2 ?? src.secondLastName);

  return [nombre, segundoNombre, apellido1, apellido2]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
};

const buildDireccionCompleta = (src = {}) => {
  const direccion = clean(src.direccion ?? src.addressLine1);
  const referenciaDireccion = clean(
    src.referenciaDireccion ?? src.referencia ?? src.addressLine2
  );
  const municipio = clean(src.municipio);
  const provincia = clean(src.provincia);
  const pais = clean(src.pais ?? src.country) || "Cuba";

  return [direccion, referenciaDireccion, municipio, provincia, pais]
    .filter(Boolean)
    .join(" - ")
    .replace(/\s+/g, " ")
    .trim();
};

const unwrapClienteRecepcion = (data) => {
  if (!data) return null;

  if (data?.clienterecepcion && !Array.isArray(data.clienterecepcion)) {
    return data.clienterecepcion;
  }

  if (data?.cliente) return data.cliente;
  if (data?.data) return data.data;

  return data;
};

const unwrapClientesRecepcion = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.clienterecepcion)) return data.clienterecepcion;
  if (Array.isArray(data?.clientes)) return data.clientes;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;

  if (Array.isArray(data?.clienterecepcion?.data)) {
    return data.clienterecepcion.data;
  }

  if (Array.isArray(data?.clientes?.data)) {
    return data.clientes.data;
  }

  return [];
};

export const normalizarClienteRecepcionPayload = (payload = {}) => {
  const src = payload || {};

  const nombre = clean(src.nombre ?? src.firstName);
  const segundoNombre = clean(src.segundoNombre ?? src.middleName);
  const apellido1 = clean(src.apellido1 ?? src.lastName);
  const apellido2 = clean(src.apellido2 ?? src.secondLastName);

  const direccion = clean(src.direccion ?? src.addressLine1);
  const referenciaDireccion = clean(
    src.referenciaDireccion ?? src.referencia ?? src.addressLine2
  );
  const provincia = clean(src.provincia);
  const municipio = clean(src.municipio);
  const zonaLogistica = clean(src.zonaLogistica).toUpperCase();
  const pais = clean(src.pais ?? src.country) || "Cuba";

  const data = {
    ...src,

    // Código interno
    codrecep: clean(src.codrecep),

    // Nombre
    nombre,
    segundoNombre,
    apellido1,
    apellido2,
    nombreCompleto: clean(src.nombreCompleto) || buildNombreCompleto(src),

    // Documento Cuba
    carnetIdentidad: clean(
      src.carnetIdentidad ??
        src.carnet ??
        src.ci ??
        src.documento ??
        src.idDocumento
    ),

    // Contacto
    telefonoPrincipal: clean(
      src.telefonoPrincipal ??
        src.telefono ??
        src.mobile ??
        src.celular ??
        src.celular1
    ),
    email: clean(src.email ?? src.correo),

    // Dirección Cuba
    direccion,
    referenciaDireccion,
    provincia,
    municipio,
    zonaLogistica,
    pais,
    direccionCompleta:
      clean(src.direccionCompleta) || buildDireccionCompleta(src),

    activo: src.activo ?? true,
  };

  if (!data.codrecep) {
    delete data.codrecep;
  }

  return data;
};

/* -------------------------- CLIENTES RECEPCIÓN --------------------------- */

// GET /clienterecepcion?page=1&limit=50&q=...
export async function getClientesRecepcion({
  page = 1,
  limit = 50,
  q = "",
  signal,
} = {}) {
  const data = await req("GET", p("clienterecepcion"), {
    params: {
      page,
      limit,
      q: String(q || "").trim(),
    },
    signal,
  });

  const clientes = unwrapClientesRecepcion(data);

  return {
    clientes,
    clienterecepcion: clientes,
    total: Number(data?.total ?? clientes.length ?? 0),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
  };
}

export async function getClienteRecepcionById(id, { signal } = {}) {
  const data = await req("GET", p(`clienterecepcion/${id}`), { signal });
  return unwrapClienteRecepcion(data);
}

export async function postClienteRecepcion(payload, { signal } = {}) {
  const data = normalizarClienteRecepcionPayload(payload);

  if (debug) {
    console.log("🟢 postClienteRecepcion PAYLOAD NORMALIZADO:", data);
  }

  return req("POST", p("clienterecepcion"), {
    data,
    signal,
  });
}

export async function putClienteRecepcion(id, payload, { signal } = {}) {
  const data = normalizarClienteRecepcionPayload(payload);

  if (debug) {
    console.log("🟣 putClienteRecepcion ID:", id);
    console.log("🟣 putClienteRecepcion PAYLOAD ORIGINAL:", payload);
    console.log("🟢 putClienteRecepcion PAYLOAD NORMALIZADO:", data);
    console.log(
      "🌐 putClienteRecepcion URL:",
      `${BASE_URL}${p(`clienterecepcion/${id}`)}`
    );
  }

  return req("PUT", p(`clienterecepcion/${id}`), {
    data,
    signal,
  });
}

export async function deleteClienteRecepcion(id, { signal } = {}) {
  return req("DELETE", p(`clienterecepcion/${id}`), { signal });
}

export async function getClienteRecepcionByCodigo(codrecep, { signal } = {}) {
  const codigo = String(codrecep || "").trim();

  if (!codigo) return null;

  const data = await req("GET", p(`clienterecepcion/codigo/${codigo}`), {
    signal,
  });

  return unwrapClienteRecepcion(data);
}

// Compatible con:
// buscarClientesRecepcion({ q: "ana", limit: 20 })
// buscarClientesRecepcion("ana", 20)
export async function buscarClientesRecepcion(arg1 = {}, arg2 = 20) {
  let q = "";
  let limit = 20;
  let signal;

  if (typeof arg1 === "string" || typeof arg1 === "number") {
    q = String(arg1 || "").trim();
    limit = Number(arg2 || 20);
  } else {
    q = String(arg1?.q || "").trim();
    limit = Number(arg1?.limit || 20);
    signal = arg1?.signal;
  }

  if (!q || q.length < 2) return [];

  const data = await req("GET", p("clienterecepcion/buscar"), {
    params: {
      q,
      limit,
    },
    signal,
  });

  const clientes = unwrapClientesRecepcion(data);

  if (debug) {
    console.log("🔎 buscarClientesRecepcion q:", q);
    console.log("🔎 clientes recepción encontrados:", clientes);
  }

  return clientes;
}

/* ---------------------- DOCUMENTOS CLIENTE RECEPCIÓN ---------------------- */

export async function subirDocumentoClienteRecepcion(
  id,
  tipo,
  file,
  { signal } = {}
) {
  const formData = new FormData();

  // Debe coincidir con upload.single("documento") del backend
  formData.append("documento", file);

  return req("POST", p(`clienterecepcion/${id}/documentos/${tipo}`), {
    data: formData,
    signal,
  });
}

export async function eliminarDocumentoClienteRecepcion(
  id,
  tipo,
  { signal } = {}
) {
  return req("DELETE", p(`clienterecepcion/${id}/documentos/${tipo}`), {
    signal,
  });
}